import { EllipsisVertical, UserSearch, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { UserDisplay } from '@/components'
import { triggerToast } from '@/components/Toast'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui'
import { useActionableCommentStore } from '@/features/actionable-comment'
import { useAuthStore } from '@/features/auth/authStore'
import { useDocumentStore } from '@/features/document'
import { ERoleType } from '@/features/user-management/constants'
import { useCurrentUser } from '@/hooks/useAuth'
import { ResponseThread, ThreadActionableType } from '@/schema'
import { cn } from '@/utils/common'

import { COMMENT_STATUS_STYLES, ECommentStatus } from '../../constants'
import {
  useMarkCommentThreadResolved,
  useMarkThreadAsRead,
} from '../../hooks/hooks'
import { useCommentStore } from '../../store'
import { extractMentionedUserIds } from '../../utils/mentionUtils'
import { formatRelativeTime } from '../../utils/timeFormat'
import { TeamworkSyncStatus } from '../TeamworkSyncStatus'

import { ExpandableCommentText } from './ExpandableCommentText'

const menuItemClassName =
  'text-sm text-primary-900 font-medium pl-4 hover:bg-gray-100 px-2 py-2'

const CommentItem: React.FC<{ comment: ResponseThread }> = ({ comment }) => {
  const { t } = useTranslation()

  const {
    activeCommentId,
    setActiveCommentId,
    setShowCommentDetail,
    updateCommentReadStatus,
    setEditingComment,
    setDeleteDialogOpen,
    setDeletingThread,
  } = useCommentStore()
  const { activeDocumentId, getIsLatestVersion } = useDocumentStore()
  const { setActionableDialogOpen, setNotActionableDialogOpen } =
    useActionableCommentStore()

  const { data: currentUser } = useCurrentUser()
  const { id } = useParams<{ id: string }>()
  const isOrgAdmin = currentUser?.roles?.some(
    (r) => r.role_name === ERoleType.ORGANIZATION_ADMIN
  )
  const getListDLViewerHasAccess = useAuthStore(
    (state) => state.getListDLViewerHasAccess
  )
  const getListDLReviewerHasAccess = useAuthStore(
    (state) => state.getListDLReviewerHasAccess
  )
  const getListPrjProjectLeadHasAccess = useAuthStore(
    (state) => state.getListPrjProjectLeadHasAccess
  )
  const listDlReviewerHasAccess = getListDLReviewerHasAccess() as string[]
  const listDLViewerHasAccess = getListDLViewerHasAccess() as string[]
  const listProjectProjectLeadHasAccess = getListPrjProjectLeadHasAccess()

  const isReviewer = listDlReviewerHasAccess.includes(id as string)
  const isViewer = listDLViewerHasAccess.includes(id as string)
  const isViewerOrReviewer = isViewer || isReviewer

  const isProjectLeadOfProject = useMemo(
    () =>
      comment.project?.id
        ? listProjectProjectLeadHasAccess.includes(comment.project.id)
        : false,
    [comment.project, listProjectProjectLeadHasAccess]
  )

  const [isMenuOpen, setIsMenuOpen] = useState<{ id: string; open: boolean }>({
    id: '',
    open: false,
  })

  const isLatestVersion = useMemo(
    () => (activeDocumentId ? getIsLatestVersion(activeDocumentId) : true),
    [activeDocumentId, getIsLatestVersion]
  )

  const isAuthor = useMemo(
    () => currentUser?.id === comment.created_by,
    [comment.created_by, currentUser?.id]
  )
  const isOrgAdminOrProjectLeadOrAuthor = useMemo(
    () => isOrgAdmin || isProjectLeadOfProject || isAuthor,
    [isOrgAdmin, isProjectLeadOfProject, isAuthor]
  )

  const isActive = useMemo(
    () => activeCommentId === comment.id,
    [activeCommentId, comment.id]
  )

  const commentRef = useRef<HTMLButtonElement>(null)
  const markAsResolvedMutation = useMarkCommentThreadResolved(
    comment.thread_id,
    comment.document_version_id
  )
  const markThreadAsReadMutation = useMarkThreadAsRead(
    comment.document_version_id,
    comment.id
  )

  useEffect(() => {
    if (isActive && commentRef.current) {
      commentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }, [isActive])

  const handleCommentClick = () => {
    setActiveCommentId(comment.id)
    setShowCommentDetail(true)

    if (comment?.has_unread_replies) {
      updateCommentReadStatus(comment.document_version_id, comment.id)
      markThreadAsReadMutation.mutate(comment.thread_id)
    }

    const commentAvatar = document.querySelector(
      `[data-comment-avatar="${comment.id}"]`
    )
    if (commentAvatar) {
      commentAvatar.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }

  const handleMarkAsActionable = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()

      const mentionedUserIds = extractMentionedUserIds(comment.content)
      const assigneeId = mentionedUserIds?.[0] || null

      setActionableDialogOpen({
        open: true,
        threadId: comment.thread_id,
        assigneeId,
      })
    },
    [comment.content, comment.thread_id, setActionableDialogOpen]
  )

  const handleChangeToActionable = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()

      const mentionedUserIds = extractMentionedUserIds(comment.content)
      const assigneeId = mentionedUserIds?.[0] || null

      setActionableDialogOpen({
        open: true,
        threadId: comment.thread_id,
        assigneeId,
        isChangeToActionable: true,
      })
    },
    [comment.content, comment.thread_id, setActionableDialogOpen]
  )

  const handleMarkAsNotActionable = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      setNotActionableDialogOpen(true, comment.thread_id)
    },
    [comment.thread_id, setNotActionableDialogOpen]
  )

  const isAllowActionable = useMemo(
    () => comment.actionable_type === ThreadActionableType.NONE,
    [comment.actionable_type]
  )

  const isAllowChangeToActionable = useMemo(
    () => comment.actionable_type === ThreadActionableType.NOT_ACTIONABLE,
    [comment.actionable_type]
  )

  const renderTeamworkStatus = useCallback(() => {
    if (isAllowActionable) {
      return (
        <DropdownMenu>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 p-0 bg-transparent border-none cursor-pointer text-foreground-fg-brand-secondary-alt"
                  aria-label="Not review"
                >
                  <UserSearch className="size-4" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="left" className="z-40">
              Not review
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent
            align="end"
            className="sm:w-[130px] sm:min-w-none"
          >
            <DropdownMenuItem
              onClick={handleMarkAsActionable}
              className={menuItemClassName}
            >
              <span>Actionable</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={handleMarkAsNotActionable}
              className={menuItemClassName}
            >
              <span>Not actionable</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }

    if (isAllowChangeToActionable)
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-foreground-fg-brand-secondary-alt border-none bg-transparent p-0 cursor-pointer"
              aria-label="Actionable"
              onClick={handleChangeToActionable}
            >
              <X className="size-4 text-warning-primary-600" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="z-40">
            Actionable
          </TooltipContent>
        </Tooltip>
      )

    return <TeamworkSyncStatus thread={comment} />
  }, [
    comment,
    handleChangeToActionable,
    handleMarkAsActionable,
    handleMarkAsNotActionable,
    isAllowActionable,
    isAllowChangeToActionable,
  ])

  const styles = useMemo(
    () =>
      COMMENT_STATUS_STYLES[
        comment.thread_status as keyof typeof COMMENT_STATUS_STYLES
      ],
    [comment.thread_status]
  )
  const relativeTime = useMemo(
    () => (comment.created_at ? formatRelativeTime(comment.created_at) : ''),
    [comment.created_at]
  )

  const isAllowAction = useMemo(
    () =>
      isLatestVersion &&
      (isAuthor || comment.thread_status !== ECommentStatus.RESOLVED) &&
      !isViewer,
    [isLatestVersion, isAuthor, comment.thread_status, isViewer]
  )
  const isActiveMenu = useMemo(
    () => isActive || (isMenuOpen.id === comment.id && isMenuOpen.open),
    [isActive, isMenuOpen.id, isMenuOpen.open, comment.id]
  )

  const isAllowReopen = useMemo(
    () =>
      // only org admin, project lead of project and author can see reopen option when the thread is resolved
      isOrgAdminOrProjectLeadOrAuthor &&
      comment.thread_status === ECommentStatus.RESOLVED,
    [comment.thread_status, isOrgAdminOrProjectLeadOrAuthor]
  )

  const isAllowEdit = useMemo(
    () =>
      (isAuthor || isOrgAdmin) &&
      comment.thread_status !== ECommentStatus.RESOLVED,
    [isAuthor, isOrgAdmin, comment.thread_status]
  )

  const isAllowReply = useMemo(
    () => comment.thread_status !== ECommentStatus.RESOLVED,
    [comment.thread_status]
  )

  const isAllowResolved = useMemo(
    () =>
      // org admin, project lead of project and author can mark as resolved
      isOrgAdminOrProjectLeadOrAuthor &&
      comment.thread_status !== ECommentStatus.RESOLVED,
    [comment.thread_status, isOrgAdminOrProjectLeadOrAuthor]
  )

  const isAllowDelete = useMemo(
    () =>
      (isOrgAdmin || isAuthor) &&
      comment.thread_status !== ECommentStatus.RESOLVED,
    [comment.thread_status, isOrgAdmin, isAuthor]
  )

  const handleReopenThread = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()

    markAsResolvedMutation.mutate('open', {
      onSuccess: () => {
        triggerToast('success', t('tflViewer.comment.messages.reopenSuccess'))
      },
      onError: () => {
        triggerToast('error', t('tflViewer.comment.messages.errorGeneric'))
      },
    })
  }

  const handleMarkAsResolved = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()

    markAsResolvedMutation.mutate('resolved', {
      onSuccess: () => {
        triggerToast('success', t('tflViewer.comment.messages.resolveSuccess'))
      },
      onError: () => {
        triggerToast('error', t('tflViewer.comment.messages.errorGeneric'))
      },
    })
  }

  const handleDeleteComment = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()

    setDeletingThread({
      threadId: comment.thread_id,
      documentVersionId: comment.document_version_id,
    })
    setDeleteDialogOpen(true)
  }

  return (
    <button
      type="button"
      ref={commentRef}
      data-comment-id={comment.id}
      className={cn(
        'border border-solid border-secondary rounded-xl bg-primary w-full flex flex-col gap-2.5 p-3 cursor-pointer transition-colors relative group',
        'hover:border-brand'
      )}
      onClick={handleCommentClick}
    >
      <div className="flex gap-1.5 justify-center group min-h-9">
        <span
          className={cn(
            'inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium border border-solid h-6',
            styles.bg,
            styles.text,
            styles.border
          )}
        >
          {comment.thread_status === 'open' ? 'Open' : 'Resolved'}
        </span>
        <span>
          {isAuthor ? (
            'Me'
          ) : (
            <UserDisplay
              user={comment.created_by_user}
              showAvatar={false}
              textClassName="text-xs text-left text-tertiary-600"
            />
          )}
        </span>

        <div className="flex items-center gap-2 ml-auto h-4.5">
          {isActive && (
            <div
              className="rounded-full size-3 min-w-3 bg-foreground-fg-brand-primary-alt"
              style={{
                border: '1.5px solid var(--Colors-Background-bg-primary)',
              }}
            />
          )}

          <div className="text-xs font-medium shrink-0 text-tertiary-600">
            {relativeTime}
          </div>
        </div>
        {isAllowAction && (
          <div
            className={cn(
              isActiveMenu ? 'size-auto' : 'size-0',
              'group-hover:size-auto overflow-hidden'
            )}
          >
            <DropdownMenu
              open={isMenuOpen.id === comment.id && isMenuOpen.open}
              onOpenChange={(open) => setIsMenuOpen({ id: comment.id, open })}
            >
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center justify-center p-0 bg-transparent border-none cursor-pointer size-4"
                      aria-label="More options"
                    >
                      <EllipsisVertical
                        size={16}
                        className="size-4 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500"
                      />
                    </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent className="z-40">More options</TooltipContent>
              </Tooltip>
              <DropdownMenuContent
                align="end"
                className="sm:w-[130px] sm:min-w-none"
              >
                {isAllowReopen && (
                  <DropdownMenuItem
                    onClick={handleReopenThread}
                    aria-label="Reopen thread"
                    className={menuItemClassName}
                  >
                    <span>Reopen</span>
                  </DropdownMenuItem>
                )}
                {isAllowEdit && (
                  <DropdownMenuItem
                    onClick={() => {
                      setEditingComment(comment)
                    }}
                    aria-label="Edit comment"
                    className={menuItemClassName}
                  >
                    <span>Edit</span>
                  </DropdownMenuItem>
                )}
                {isAllowReply && (
                  <DropdownMenuItem
                    onClick={handleCommentClick}
                    aria-label="Reply to comment"
                    className={menuItemClassName}
                  >
                    <span>Reply</span>
                  </DropdownMenuItem>
                )}
                {isAllowResolved && (
                  <DropdownMenuItem
                    onClick={handleMarkAsResolved}
                    aria-label="Mark comment as resolved"
                    className={menuItemClassName}
                  >
                    <span>Resolved</span>
                  </DropdownMenuItem>
                )}
                {isAllowDelete && (
                  <DropdownMenuItem
                    onClick={handleDeleteComment}
                    aria-label="Delete comment"
                    className={menuItemClassName}
                  >
                    <span>Delete</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col items-start justify-between gap-0.5">
        <ExpandableCommentText
          content={comment.content}
          isBold={comment?.has_unread_replies}
        />
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center flex-1 gap-2">
          <div className="text-xs text-primary-900">
            {useMemo(() => {
              const totalReplies = comment.total_replies ?? 0
              return `${totalReplies} ${totalReplies > 1 ? 'replies' : 'reply'}`
            }, [comment.total_replies])}
          </div>
          {comment.has_unread_replies && comment.unread_replies ? (
            <>
              <div className="w-px h-3 border border-r-0 border-solid border-secondary border-y-0" />
              <div className="text-xs text-brand-tertiary-600">
                {comment.unread_replies} unread
              </div>
            </>
          ) : null}
        </div>
        {!isViewerOrReviewer && renderTeamworkStatus()}
      </div>
    </button>
  )
}

export default CommentItem
